import { PRIORITY_IDS } from 'common/components/AdvancedSearch/AdvancedSearch.constants';
import { DEFAULT_AI_SEARCH_ACCURACY } from 'constants/defaultAiSearchAccuracy';
import capitalize from 'lodash/capitalize';
import { API_CLIENT_NAMES } from 'services/API';
import strings from 'strings';
import { FilterType } from 'UI/constants/defaults';
import { Endpoints } from 'UI/constants/endpoints';
import { FeatureFlags } from 'UI/constants/featureFlags';
import { hasFeatureFlag } from 'UI/utils/featureFlags';
import { Groupers, LabelRenderers, OptionRenderers } from 'UI/utils/renderers';

import { ENDPOINTS } from '../../ResumeSearch.constants';

import {
  formatNameOptionSubtitle,
  keepFirstSelectionByName,
  stripNonDigits
} from './Filters.utils';

const FILTER_NAMES = strings.resumeSearch.filters.names;
const FILTER_GROUP_NAMES = strings.resumeSearch.filters.groups;
const FILTER_PLACEHOLDERS = strings.resumeSearch.filters.placeholders;

const renderNameOption = OptionRenderers.globalSearchPerson('title');

export const FILTER_CONFIG = [
  {
    title: FILTER_NAMES.createdAt,
    type: FilterType.DateRange,
    name: 'createdAt',
    fieldName: 'createdAt',
    isDateRange: true
  },
  {
    title: FILTER_NAMES.AISearch,
    type: FilterType.Search,
    name: 'ai-search',
    fieldName: 'summary',
    placeholder: strings.resumeSearch.filters.aiSmartSearchPlaceholder,
    formatValue: value => {
      if (!value || !value.aiSummary) return null;
      const smart = hasFeatureFlag(FeatureFlags.CandidatesSearchAccuracy)
        ? { accuracy: value.accuracy, text: value.aiSummary }
        : { text: value.aiSummary };
      return {
        item: {
          value: { smart }
        },
        priority: 'must_have'
      };
    },
    getValue: value => {
      if (!value || !value?.item?.value.smart?.text) return null;
      return {
        accuracy: value?.item?.value.smart?.accuracy ?? DEFAULT_AI_SEARCH_ACCURACY,
        aiSummary: value?.item?.value.smart?.text
      };
    }
  },
  {
    title: FILTER_NAMES.industry,
    type: FilterType.AdvancedSearch,
    name: 'industry',
    endpoint: ENDPOINTS.Industries,
    placeholder: FILTER_PLACEHOLDERS.industry,
    defaultPriority: PRIORITY_IDS.CanHave,
    typeaheadParams: { limitKey: 'size', sort: 'key' },
    typeahead: false,
    typeaheadLimit: 50
  },
  {
    title: FILTER_NAMES.specialty,
    type: FilterType.AdvancedSearch,
    name: 'specialty',
    fieldName: 'basics.specialty.title',
    endpoint: Endpoints.Specialties,
    apiClient: API_CLIENT_NAMES.Primary,
    renderOption: OptionRenderers.title,
    getOptionLabel: LabelRenderers.specialty,
    groupBy: Groupers.byIndustry,
    placeholder: FILTER_PLACEHOLDERS.specialty,
    defaultPriority: PRIORITY_IDS.CanHave,
    typeaheadParams: { queryKey: 'keyword', sort: 'key' },
    formatSelectedItems: selectedItems =>
      selectedItems.map(item => ({ ...item, item: { value: item.item.title, ...item.item } })),
    typeahead: true
  },
  {
    title: FILTER_NAMES.subspecialty,
    type: FilterType.AdvancedSearch,
    name: 'subspecialty',
    fieldName: 'basics.subspecialty.title',
    endpoint: Endpoints.SubspecialtiesSearch,
    apiClient: API_CLIENT_NAMES.Primary,
    renderOption: OptionRenderers.subspecialty,
    getOptionLabel: LabelRenderers.subspecialty,
    placeholder: FILTER_PLACEHOLDERS.subspecialty,
    defaultPriority: PRIORITY_IDS.CanHave,
    formatSelectedItems: selectedItems =>
      selectedItems.map(item => ({ ...item, item: { value: item.item.title, ...item.item } })),
    typeaheadParams: { queryKey: 'keyword', sort: 'key' },
    typeahead: true
  },
  {
    title: FILTER_NAMES.skills,
    type: FilterType.AdvancedSearch,
    name: 'skills',
    endpoint: ENDPOINTS.Skills,
    placeholder: FILTER_PLACEHOLDERS.skills,
    defaultPriority: PRIORITY_IDS.CanHave,
    typeaheadParams: { limitKey: 'size', sort: 'score' },
    typeahead: true
  },
  {
    title: FILTER_NAMES.activities,
    type: FilterType.AdvancedSearch,
    name: 'activities',
    preferencesFieldName: 'lastActivityByType.date',
    preferencesEndpoint: Endpoints.ActivityTimeFilters,
    preferencesApiClient: API_CLIENT_NAMES.Primary,
    endpoint: ENDPOINTS.LastActivityType,
    placeholder: FILTER_PLACEHOLDERS.activities,
    defaultPriority: PRIORITY_IDS.CanHave,
    typeaheadParams: { limitKey: 'size', sort: 'key' },
    typeahead: false,
    typeaheadLimit: 50
  },
  {
    title: FILTER_NAMES.languages,
    type: FilterType.AdvancedSearch,
    name: 'languages',
    endpoint: ENDPOINTS.Languages,
    placeholder: FILTER_PLACEHOLDERS.languages,
    defaultPriority: PRIORITY_IDS.CanHave,
    typeaheadParams: { limitKey: 'size', sort: 'score' },
    typeahead: true,
    typeaheadLimit: 20
  },
  {
    title: FILTER_NAMES.companies,
    type: FilterType.AdvancedSearch,
    name: 'companies',
    endpoint: ENDPOINTS.ProfessionalExperiencesCompanies,
    placeholder: FILTER_PLACEHOLDERS.companies,
    defaultPriority: PRIORITY_IDS.CanHave,
    typeaheadParams: { limitKey: 'size', sort: 'key' },
    typeahead: true
  },
  {
    title: FILTER_NAMES.positions,
    type: FilterType.AdvancedSearch,
    name: 'positions',
    endpoint: ENDPOINTS.ProfessionalExperiencesTitles,
    placeholder: FILTER_PLACEHOLDERS.positions,
    defaultPriority: PRIORITY_IDS.CanHave,
    typeaheadParams: { limitKey: 'size', sort: 'score' },
    typeahead: true
  },
  {
    title: FILTER_NAMES.keywords,
    type: FilterType.AdvancedSearch,
    name: 'keywords',
    endpoint: ENDPOINTS.Keywords,
    placeholder: FILTER_PLACEHOLDERS.keywords,
    defaultPriority: PRIORITY_IDS.CanHave,
    typeaheadParams: { limitKey: 'size', sort: 'score' },
    typeahead: true
  },
  {
    title: FILTER_NAMES.name,
    type: FilterType.AdvancedSearch,
    name: 'names',
    endpoint: ENDPOINTS.Names,
    placeholder: FILTER_PLACEHOLDERS.names,
    defaultPriority: PRIORITY_IDS.CanHave,
    typeahead: true,
    typeaheadLimit: 20,
    typeaheadParams: { limitKey: 'size' },
    getOptionId: option => option.metadata?.email ?? option.value,
    formatSelectedItems: keepFirstSelectionByName,
    renderOption: option =>
      renderNameOption({
        title: option.value,
        email: option.metadata?.email,
        subtitle: formatNameOptionSubtitle(option.metadata)
      })
  },
  {
    title: FILTER_NAMES.jobFunctions,
    type: FilterType.AdvancedSearch,
    name: 'jobFunctions',
    endpoint: ENDPOINTS.JobFunctions,
    placeholder: FILTER_PLACEHOLDERS.jobFunctions,
    defaultPriority: PRIORITY_IDS.CanHave,
    typeaheadParams: { limitKey: 'size', sort: 'key' },
    typeahead: true
  },
  {
    title: FILTER_NAMES.teams,
    type: FilterType.AdvancedSearch,
    name: 'teams',
    endpoint: ENDPOINTS.Teams,
    placeholder: FILTER_PLACEHOLDERS.teams,
    defaultPriority: PRIORITY_IDS.CanHave,
    typeaheadParams: { limitKey: 'size', sort: 'key' },
    typeahead: false
  },
  {
    title: FILTER_NAMES.recruiters,
    type: FilterType.AdvancedSearch,
    name: 'recruiters',
    endpoint: ENDPOINTS.Recruiters,
    placeholder: FILTER_PLACEHOLDERS.recruiters,
    defaultPriority: PRIORITY_IDS.CanHave,
    typeaheadParams: { limitKey: 'size', sort: 'key' },
    typeahead: true
  },
  {
    title: FILTER_NAMES.professionalAssociations,
    type: FilterType.AdvancedSearch,
    name: 'professionalAssociationsIssuingOrganizations',
    endpoint: ENDPOINTS.ProfessionalAssociationsIssuingOrganizations,
    placeholder: FILTER_PLACEHOLDERS.professionalAssociations,
    defaultPriority: PRIORITY_IDS.CanHave,
    typeaheadParams: { limitKey: 'size', sort: 'score' },
    typeahead: true
  },
  {
    title: FILTER_NAMES.certifications,
    type: FilterType.AdvancedSearch,
    name: 'certifications',
    endpoint: ENDPOINTS.CertificationsDescriptions,
    placeholder: FILTER_PLACEHOLDERS.certifications,
    defaultPriority: PRIORITY_IDS.CanHave,
    typeaheadParams: { limitKey: 'size', sort: 'score' },
    typeahead: true
  },
  {
    title: FILTER_NAMES.certifyingOrganizations,
    type: FilterType.AdvancedSearch,
    name: 'certifyingOrganizations',
    endpoint: ENDPOINTS.CertificationsIssuingOrganizations,
    typeahead: true
  },
  {
    title: FILTER_NAMES.degrees,
    type: FilterType.AdvancedSearch,
    name: 'degrees',
    endpoint: ENDPOINTS.EducationsDegrees,
    placeholder: FILTER_PLACEHOLDERS.degrees,
    defaultPriority: PRIORITY_IDS.CanHave,
    typeaheadParams: { limitKey: 'size', sort: 'score' },
    typeahead: true
  },
  {
    title: FILTER_NAMES.educationsIssuingOrganizations,
    type: FilterType.AdvancedSearch,
    name: 'educationsIssuingOrganizations',
    endpoint: ENDPOINTS.EducationsIssuingOrganizations,
    placeholder: FILTER_PLACEHOLDERS.educationsIssuingOrganizations,
    defaultPriority: PRIORITY_IDS.CanHave,
    typeaheadParams: { limitKey: 'size', sort: 'score' },
    typeahead: true
  },
  {
    title: FILTER_NAMES.majors,
    type: FilterType.AdvancedSearch,
    name: 'majors',
    endpoint: ENDPOINTS.EducationsMajors,
    placeholder: FILTER_PLACEHOLDERS.majors,
    defaultPriority: PRIORITY_IDS.CanHave,
    typeaheadParams: { limitKey: 'size', sort: 'score' },
    typeahead: true
  },
  {
    title: FILTER_NAMES.states,
    type: FilterType.AdvancedSearch,
    name: 'states',
    endpoint: ENDPOINTS.LocationsStates,
    placeholder: FILTER_PLACEHOLDERS.states,
    defaultPriority: PRIORITY_IDS.CanHave,
    typeaheadParams: { limitKey: 'size', sort: 'key' },
    typeahead: true
  },
  {
    title: FILTER_NAMES.cities,
    type: FilterType.City,
    name: 'cities',
    fieldName: 'basics.location.cityState',
    coordinatesFieldName: 'basics.location.coordinates',
    endpoint: ENDPOINTS.LocationsCities,
    groupBy: option => option.metadata?.state?.title
  },
  {
    title: FILTER_NAMES.statuses,
    type: FilterType.AdvancedSearch,
    name: 'statuses',
    endpoint: ENDPOINTS.Statuses,
    placeholder: FILTER_PLACEHOLDERS.statuses,
    defaultPriority: PRIORITY_IDS.CanHave,
    typeaheadParams: { limitKey: 'size', sort: 'key' },
    typeahead: false
  },
  {
    title: FILTER_NAMES.types,
    type: FilterType.AdvancedSearch,
    name: 'types',
    endpoint: ENDPOINTS.Types,
    placeholder: FILTER_PLACEHOLDERS.types,
    defaultPriority: PRIORITY_IDS.CanHave,
    typeaheadParams: { limitKey: 'size', sort: 'key' },
    typeahead: false
  },
  {
    title: strings.shared.filters.optOutPlaceHolder,
    type: FilterType.AdvancedSearch,
    name: 'emailSubscriptionStatus',
    endpoint: ENDPOINTS.EmailSubscriptionStatuses,
    placeholder: FILTER_PLACEHOLDERS.emailSubscriptionStatus,
    defaultPriority: PRIORITY_IDS.MustHave,
    typeaheadParams: { limitKey: 'size', sort: 'key' },
    typeahead: false,
    typeaheadLimit: 20,
    isSingleSelect: true
  },
  {
    title: strings.shared.filters.emailValidationPlaceHolder,
    type: FilterType.AdvancedSearch,
    name: 'emailValidation',
    endpoint: ENDPOINTS.EmailValidations,
    placeholder: FILTER_PLACEHOLDERS.statuses,
    defaultPriority: PRIORITY_IDS.CanHave,
    typeaheadParams: { limitKey: 'size', sort: 'key' },
    typeahead: false,
    typeaheadLimit: 20,
    getOptionLabel: option => capitalize(option.value)
  },
  {
    title: FILTER_NAMES.phoneNumber,
    type: FilterType.Text,
    name: 'phoneNumber',
    fieldName: 'phone_number',
    placeholder: FILTER_PLACEHOLDERS.phoneNumber,
    transform: stripNonDigits
  },
  {
    title: FILTER_NAMES.email,
    type: FilterType.Text,
    name: 'email',
    fieldName: 'basics.email',
    placeholder: FILTER_PLACEHOLDERS.email
  },
  {
    title: FILTER_NAMES.url,
    type: FilterType.Text,
    name: 'resumeUrls',
    fieldName: 'urls',
    placeholder: FILTER_PLACEHOLDERS.url
  },
  {
    title: FILTER_NAMES.workPreferences,
    type: FilterType.AdvancedSearch,
    name: 'workPreferences',
    endpoint: ENDPOINTS.WorkPreferences,
    placeholder: FILTER_PLACEHOLDERS.workPreferences,
    defaultPriority: PRIORITY_IDS.CanHave,
    typeaheadParams: { limitKey: 'size', sort: 'key' },
    typeahead: false
  },
  {
    title: FILTER_NAMES.timesToStart,
    type: FilterType.AdvancedSearch,
    name: 'timesToStart',
    endpoint: ENDPOINTS.TimesToStart,
    placeholder: FILTER_PLACEHOLDERS.timesToStart,
    defaultPriority: PRIORITY_IDS.CanHave,
    typeaheadParams: { limitKey: 'size', sort: 'key' },
    typeahead: false
  },
  {
    title: FILTER_NAMES.reasonsForLooking,
    type: FilterType.AdvancedSearch,
    name: 'reasonsForLooking',
    endpoint: ENDPOINTS.ReasonsForLooking,
    placeholder: FILTER_PLACEHOLDERS.reasonsForLooking,
    defaultPriority: PRIORITY_IDS.CanHave,
    typeaheadParams: { limitKey: 'size', sort: 'key' },
    typeahead: false
  },
  {
    title: FILTER_NAMES.openToRelocation,
    type: FilterType.AdvancedSearch,
    name: 'openToRelocation',
    endpoint: ENDPOINTS.OpenToRelocation,
    placeholder: FILTER_PLACEHOLDERS.openToRelocation,
    defaultPriority: PRIORITY_IDS.MustHave,
    typeaheadParams: { limitKey: 'size', sort: 'key' },
    isSingleSelect: true,
    typeahead: false
  },
  {
    title: FILTER_NAMES.salary,
    type: FilterType.Salary,
    name: 'salary',
    fieldName: 'datasheet.data.workPreferences.salaryRanges.idealSalary',
    salaryTypeFieldName: 'datasheet.data.workPreferences.salary',
    endpoint: ENDPOINTS.SalaryTypes
  },
  {
    title: FILTER_NAMES.relocationDestinations,
    type: FilterType.AdvancedSearch,
    name: 'relocationDestinations',
    endpoint: ENDPOINTS.RelocationDestinations,
    placeholder: FILTER_PLACEHOLDERS.relocationDestinations,
    defaultPriority: PRIORITY_IDS.CanHave,
    typeaheadParams: { limitKey: 'size', sort: 'score' },
    typeahead: true,
    getOptionId: option => option.metadata.id,
    getOptionLabel: option =>
      option.metadata.is_state
        ? option.metadata.title
        : `${option.metadata.title}, ${option.metadata.state}`
  }
];

export const PINNED_FILTER_NAME = 'ai-search';

export const FILTER_GROUPS = [
  {
    id: 'industry',
    name: FILTER_GROUP_NAMES.industry,
    filters: ['industry', 'specialty', 'subspecialty', 'jobFunctions']
  },
  {
    id: 'location',
    name: FILTER_GROUP_NAMES.location,
    filters: ['states', 'cities']
  },
  {
    id: 'experience',
    name: FILTER_GROUP_NAMES.experience,
    filters: ['companies', 'positions', 'skills']
  },
  {
    id: 'educationQualifications',
    name: FILTER_GROUP_NAMES.educationQualifications,
    filters: [
      'degrees',
      'educationsIssuingOrganizations',
      'majors',
      'certifications',
      'certifyingOrganizations',
      'professionalAssociationsIssuingOrganizations',
      'languages'
    ]
  },
  {
    id: 'activity',
    name: FILTER_GROUP_NAMES.activity,
    filters: ['activities']
  },
  {
    id: 'candidate',
    name: FILTER_GROUP_NAMES.candidate,
    filters: [
      'names',
      'statuses',
      'types',
      'keywords',
      'phoneNumber',
      'email',
      'resumeUrls',
      'emailSubscriptionStatus',
      'emailValidation'
    ]
  },
  {
    id: 'workPreferences',
    name: FILTER_GROUP_NAMES.workPreferences,
    filters: ['workPreferences', 'salary']
  },
  {
    id: 'availability',
    name: FILTER_GROUP_NAMES.availability,
    filters: ['reasonsForLooking', 'openToRelocation', 'relocationDestinations', 'timesToStart']
  },
  {
    id: 'organization',
    name: FILTER_GROUP_NAMES.organization,
    filters: ['teams', 'recruiters']
  }
];

export const FILTER_DEFAULT_VALUES = FILTER_CONFIG.reduce((acc, config) => {
  const entry = {
    fieldName: config.fieldName || null,
    preferencesFieldName: config.preferencesFieldName || null,
    value: null
  };
  if (config.salaryTypeFieldName) {
    entry.salaryTypeFieldName = config.salaryTypeFieldName;
  }
  if (config.coordinatesFieldName) {
    entry.coordinatesFieldName = config.coordinatesFieldName;
  }
  if (config.isDateRange) {
    entry.isDateRange = true;
  }
  acc[config.name] = entry;
  return acc;
}, {});

export const PRIORITY_MAP = {
  must_have: 'MUST',
  can_have: 'SHOULD',
  doesnt_have: 'NOT'
};
